import React, { forwardRef, useImperativeHandle, useState, useRef } from 'react';
import {
  Modal, ModalBody, ModalHeader, Form, FormGroup, Input, Row, Col, Label, Button as RButton, Table
} from 'reactstrap';
import { Tooltip } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import axiosInstance from '../../helpers/axios';
import closeIcon from '../../assets/images/close.png';
import downloadIcon from '../../assets/images/download.png';
import ShowDetails from '../status/ShowDetails/ShowDetails';
import { lineHeight, margin } from '@mui/system';

// ─── shared style helpers ────────────────────────────────────────────────────

const HEADER = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0 14px', height: '46px', borderBottom: '1px solid #eaecf0',
  backgroundColor: '#fff', borderTopLeftRadius: '10px', borderTopRightRadius: '10px',
};
const HEADER_TITLE = { fontSize: '14px', fontWeight: '600', color: '#101828', margin: 0 };
const FOOTER = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5rem',
  padding: '10px 14px', borderTop: '1px solid #eaecf0',
  borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px'
};
const LABEL_STYLE = { fontSize: '14px', color: '#667085', fontWeight: '600', marginBottom: '2px' };
const VALUE_STYLE = { fontSize: '14px', color: '#101828', fontWeight: '600', wordBreak: 'break-word' };
const INFO_ROW = { padding: '6px 0', borderBottom: '1px solid #f2f4f7',lineHeight:'2rem' };
const BTN_PRIMARY = {
  backgroundColor: '#D32028', color: 'white', border: 'none',
  borderRadius: '6px', fontSize: '14px', fontWeight: '500', padding: '3px 10px', cursor: 'pointer', lineHeight: '1.6',
};
const BTN_SECONDARY = {
  backgroundColor: '#fff', color: '#344054', border: '1px solid #d0d5dd',
  borderRadius: '6px', fontSize: '14px', fontWeight: '500', padding: '3px 10px', cursor: 'pointer', lineHeight: '1.6',marginRight:'20px',
};

const Pill = ({ value, threshold, higherGood = true }) => {
  if (value == null) return null;
  const pass = higherGood ? value >= threshold : value <= threshold;
  return (
    <span style={{
      backgroundColor: pass ? '#ECFDF3' : '#FEEAEB',
      color: pass ? '#12B76A' : '#D22129',
      borderRadius: '20px', padding: '2px 10px', fontSize: '14px', fontWeight: '600',
    }}>
      {typeof value === 'number' ? `${value.toFixed(2)} %` : value}
    </span>
  );
};

const Circle = ({ pass }) => (
  <span style={{
    display: 'inline-block', width: '18px', height: '18px', borderRadius: '50%',
    backgroundColor: pass ? '#12B76A' : '#D22129',
  }} />
);

const CloseBtn = ({ onClick }) => (
  <span style={{ cursor: 'pointer', lineHeight: 1 }} onClick={onClick}>
    <img src={closeIcon} alt="close" style={{ width: '20px', height: '20px' }} />
  </span>
);

// ─── component ───────────────────────────────────────────────────────────────

const IntegrationModals = forwardRef(({ user, decode, onActionComplete }, ref) => {

  // ── regen context (replaces sessionStorage) ──
  const [regenAppId, setRegenAppId] = useState('');
  const [regenUserId, setRegenUserId] = useState('');
  const [regenName, setRegenName] = useState('');
  const [mStatus, setMStatus] = useState('');
  const [regenInstituteName, setRegenInstituteName] = useState('');

  // ── digilocker ──
  const [digiOpen, setDigiOpen] = useState(false);
  const [digiKID, setDigiKID] = useState('');
  const [digiTxnId, setDigiTxnId] = useState('');
  const [digiName, setDigiName] = useState('');
  const [digiDob, setDigiDob] = useState('');
  const [digiAddress, setDigiAddress] = useState('');
  const [digiNameScore, setDigiNameScore] = useState('');
  const [digiAadhaarMatch, setDigiAadhaarMatch] = useState(null);
  const [digiAadhaarId, setDigiAadhaarId] = useState('');
  const [digiStatus, setDigiStatus] = useState('');
  const [digiApplicantName, setDigiApplicantName] = useState('');
  const [isHovered, setIsHovered] = useState(false);

  // ── selfie ──
  const [selfieOpen, setSelfieOpen] = useState(false);
  const [selfieKID, setSelfieKID] = useState('');
  const [selfieTxnId, setSelfieTxnId] = useState('');
  const [selfieDetails, setSelfieDetails] = useState(null);

  // ── agreement ──
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementInfo, setAgreementInfo] = useState(null);
  const [agreementDocDetails, setAgreementDocDetails] = useState(null);
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [agreementLoading, setAgreementLoading] = useState(false);
  const inputRef = useRef();

  // ── choose mandate form ──
  const [chooseMandateOpen, setChooseMandateOpen] = useState(false);
  const [mandateFormData, setMandateFormData] = useState({
    applicationId: '', userId: '', name: '', currentMandate: '', selectedMandate: '', instituteName: '',
  });
  const [remark, setRemark] = useState('');
  const mandateOptions = [
    { value: '1', label: 'RazorPay' },
    { value: '15', label: 'Aadhaar Mandate' },
    { value: '16', label: 'Upi Mandate' },
    { value: '22', label: 'PhonePe Upi Mandate' },
  ];

  // ── mandate details (razorpay) ──
  const [mandateOpen, setMandateOpen] = useState(false);
  const [mandateDetails, setMandateDetails] = useState(null);

  // ── upi mandate ──
  const [upiMandateOpen, setUpiMandateOpen] = useState(false);
  const [upiMandateDetails, setUpiMandateDetails] = useState(null);
  const [phonepeDetails, setPhonepeDetails] = useState(null);

  // ── aadhaar mandate ──
  const [aadharMandateOpen, setAadharMandateOpen] = useState(false);
  const [aadharMandateDetails, setAadharMandateDetails] = useState(null);

  // ── refresh confirmations ──
  const [refreshMandateOpen, setRefreshMandateOpen] = useState(false);
  const [refreshUpiMandateOpen, setRefreshUpiMandateOpen] = useState(false);

  // ── fraud ──
  const [fraudOpen, setFraudOpen] = useState(false);
  const [fraudDetails, setFraudDetails] = useState(null);

  // ── verification details (pan/ckyc/digio/cibil) ──
  const [showVerificationDetails, setShowVerificationDetails] = useState(false);
  const [userVerificationData, setUserVerificationData] = useState(null);


  // ── internal helpers ──────────────────────────────────────────────────────

  const openDigilocker = ({ applicationId, userId, name, status }) => {
    setDigiStatus(status);
    setDigiApplicantName(name || '');
    setRegenAppId(applicationId);
    setRegenUserId(userId);
    axiosInstance.get(`/digilocker/details?applicationId=${applicationId}&userId=${userId}`)
      .then(res => {
        setDigiKID(res?.data?.data?.response?.kid || '');
        setDigiTxnId(res?.data?.data?.response?.transactionId || '');
        setDigiNameScore(res?.data?.data?.response?.nameMatchScore || '');
        setDigiName(res?.data?.data?.response?.response?.aadhaar?.name || '');
        setDigiAddress(res?.data?.data?.response?.response?.aadhaar?.current_address || '');
        setDigiDob(res?.data?.data?.response?.response?.aadhaar?.dob || '');
        setDigiAadhaarId(res?.data?.data?.response?.response?.aadhaar?.id_number || '');
        setDigiAadhaarMatch(res?.data?.data?.response?.aadhaarMatch ?? null);
      }).catch(() => {});
    setDigiOpen(true);
  };

  const openSelfie = ({ applicationId, userId }) => {
    setRegenAppId(applicationId);
    setRegenUserId(userId);
    axiosInstance.get(`/integrations/details?applicationId=${applicationId}&userId=${userId}`)
      .then(res => {
        const data = res?.data?.data;
        setSelfieDetails(data);
        setSelfieKID(data?.selfieDetails?.entityId || '');
        setSelfieTxnId(data?.selfieDetails?.transactionId || '');
      }).catch(() => {});
    setSelfieOpen(true);
  };

  const openAgreement = ({ applicationId, userId, status }) => {
    setRegenAppId(applicationId);
    setRegenUserId(userId);
    setAgreementStatus(status);
    setAgreementInfo(null);
    setAgreementDocDetails(null);
    axiosInstance.post(`/agreement/downloadAgreement?userId=${userId}&applicationId=${applicationId}`)
      .then(resp => {
        if (resp?.data?.data !== undefined) {
          try { setAgreementInfo(JSON.parse(resp.data.data.info)); } catch (e) {}
          setAgreementDocDetails(resp.data.data);
        }
      }).catch(() => {});
    setAgreementOpen(true);
  };

  const openMandate = ({ applicationId, userId, name, status, cra, instituteName }) => {
    setRegenAppId(applicationId);
    setRegenUserId(userId);
    if (name) setRegenName(name);
    setMStatus(status);
    if (instituteName) setRegenInstituteName(instituteName);

    const isUpi = ['Upi Mandate Initiated', 'Upi Mandate Partial', 'Upi Mandate Failed', 'Upi Mandate Done', 
      "PhonePay Upi Initiated", "PhonePay Upi Failed", "PhonePay Upi Success", "PhonePe Upi Mandate"].includes(status);
    const isAadhar = ['Aadhaar Mandate Done'].includes(status);
    const showDetails = ['Pass', 'Upi Mandate Done', 'Aadhaar Mandate Done', 'Invalid Bank Details',  "PhonePay Upi Success",].includes(status);
    console.log('showDetails', showDetails, status, isUpi, "all");
    if (!showDetails) {
      if (status === 'Rejected') {
        axiosInstance.get(`/integrations/razorpay-details?applicationId=${applicationId}&userId=${userId}`)
          .then(resp => { setRemark(resp?.data?.data?.remark || ''); }).catch(() => {});
      }
      setMandateFormData({ applicationId, userId, name: name || '', currentMandate: status || '', selectedMandate: '', instituteName: instituteName || '' });
      setChooseMandateOpen(true);
    } else if (isAadhar) {
      axiosInstance.get(`/digio-emandates/mandate-details?applicationId=${applicationId}&userId=${userId}`)
        .then(resp => { setAadharMandateDetails(resp.data.data); setAadharMandateOpen(true); }).catch(() => {});
    } else if (isUpi) {
      let url = (status == "PhonePay Upi Success")? `/phonepay-upi/mandate-details?applicationId=${applicationId}&userId=${userId}`
        : `/mandate/mandate-details?applicationId=${applicationId}&userId=${userId}`;
      axiosInstance.get(url)
        .then(resp => {
          if (status === 'PhonePay Upi Success') { setPhonepeDetails(resp.data.data); setUpiMandateDetails(null); }
          else { setUpiMandateDetails(resp.data.data); setPhonepeDetails(null); }
          setUpiMandateOpen(true);
        }).catch(() => {});
    } else {
      axiosInstance.get(`/integrations/razorpay-details?applicationId=${applicationId}&userId=${userId}`)
        .then(resp => { setMandateDetails(resp.data.data); setMandateOpen(true); }).catch(() => {});
    }
  };

  const openFraud = ({ applicationId, userId, name }) => {
    setRegenAppId(applicationId);
    setRegenUserId(userId);
    if (name) setRegenName(name);
    setFraudDetails(null);
    axiosInstance.get(`/integrations/fraud-check?applicationId=${applicationId}&userId=${userId}`)
      .then(resp => { setFraudDetails(resp?.data?.data); }).catch(() => {});
    setFraudOpen(true);
  };

  // ── exposed API ──────────────────────────────────────────────────────────

  useImperativeHandle(ref, () => ({
    open(type, params = {}) {
      switch (type) {
        case 'digilocker': openDigilocker(params); break;
        case 'selfie':     openSelfie(params);     break;
        case 'agreement':  openAgreement(params);  break;
        case 'mandate':    openMandate(params);    break;
        case 'fraud':      openFraud(params);      break;
        default: break;
      }
    },
  }));

  // ── action handlers ──────────────────────────────────────────────────────

  const digiRetrigger = () => {
    if (!digiKID || !digiTxnId) { alert('Enter Proper Values'); return; }
    const appId = regenAppId;
    const uid = regenUserId;
    axiosInstance.post(`/digilocker/refreshDetails?applicationId=${appId}&userId=${uid}&kid=${digiKID.trim()}&transactionId=${digiTxnId.trim()}`, null)
      .then(res => { alert(res?.data?.message); onActionComplete?.(); })
      .catch(err => { alert(err?.response?.data?.message); });
  };

  const skipDigiLocker = () => {
    axiosInstance.post('/digilocker/skip', { applicationId: regenAppId, userId: regenUserId })
      .then(res => { if (res?.data?.message === 'success') { alert('DigiLocker Skipped'); onActionComplete?.(); window.location.reload(); } }).catch(() => {});
  };

  const enableDigiLocker = () => {
    axiosInstance.post('/digilocker/enable', { applicationId: regenAppId, userId: regenUserId })
      .then(res => { if (res?.data?.message === 'success') { alert('DigiLocker Enabled'); onActionComplete?.(); window.location.reload(); } }).catch(() => {});
  };

  const selfieRetrigger = () => {
    if (!selfieKID || !selfieTxnId) { alert('Enter Proper Values'); return; }
    const appId = regenAppId;
    const uid = regenUserId;
    axiosInstance.post(`/selfie/refreshDetails?applicationId=${appId}&userId=${uid}&kid=${selfieKID.trim()}&transactionId=${selfieTxnId.trim()}`, null)
      .then(res => { alert(res?.data?.message); onActionComplete?.(); setTimeout(() => window.location.reload(true), 500); })
      .catch(err => { alert(err?.response?.data?.message); });
  };

  const selfieReinitiate = () => {
    axiosInstance.post('/selfie/retrigger', { applicationId: regenAppId, userId: regenUserId })
      .then(res => { alert(res?.data?.message); onActionComplete?.(); setTimeout(() => window.location.reload(true), 500); }).catch(() => {});
  };

  const retriggerAgreement = () => {
    axiosInstance.post('/agreement/regenerate', { applicationId: regenAppId, userId: regenUserId })
      .then(res => { alert(res?.data?.message || 'Agreement Triggered'); onActionComplete?.(); }).catch(() => {});
  };

  const skipAgreement = () => {
    axiosInstance.post('/agreement/skip', { applicationId: regenAppId, userId: regenUserId })
      .then(res => { if (res?.data?.message === 'success') { alert('Agreement Skipped'); onActionComplete?.(); window.location.reload(); } }).catch(() => {});
  };

  const enableAgreement = () => {
    axiosInstance.post('/agreement/enable', { applicationId: regenAppId, userId: regenUserId })
      .then(res => { if (res?.data?.message === 'success') { alert('Agreement Enabled'); onActionComplete?.(); window.location.reload(); } }).catch(() => {});
  };

  const uploadManualAgreement = (e) => {
    const file = new FormData();
    file.append('file', e.target.files[0]);
    file.append('type', 'manual_agreement');
    axiosInstance.post('/file/upload', file).then(res => {
      axiosInstance.post('/agreement/upload-signed', {
        userId: regenUserId,
        applicationId: regenAppId,
        signedDocumentUrl: res?.data?.data?.s3Url,
      })
        .then(() => { alert('Manual Agreement Uploaded'); setAgreementOpen(false); onActionComplete?.(); setTimeout(() => window.location.reload(), 1500); })
        .catch(err => { alert(err?.response?.data?.message); });
    });
  };

  const generateAgreementCopy = async () => {
    setAgreementLoading(true);
    try {
      const res = await axiosInstance.post('/agreement/generate-copy', {
        applicationId: regenAppId,
        userId: regenUserId,
      });
      if (res?.data?.message === 'success') {
        const urlRes = await axiosInstance.get(`/file/url-content?path=${res?.data?.data?.split('.com/')[1]}`);
        if (urlRes?.data?.message === 'Successful') {
          const link = document.createElement('a');
          link.href = urlRes.data.data;
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (e) { alert('Agreement Generate failed.'); }
    finally { setAgreementLoading(false); }
  };

  const downloadAgreement = () => {
    const path = agreementDocDetails?.signedDocumentUrl?.split('.com/')?.[1];
    if (!path) return;
    axiosInstance.get(`/file/url-content?path=${path}`)
      .then(res => {
        const link = document.createElement('a');
        link.href = res.data.data;
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).catch(() => {});
  };

  const submitMandate = () => {
    axiosInstance.post('/integrations/select-mandate', {
      userId: mandateFormData.userId,
      applicationId: mandateFormData.applicationId,
      mandateStatus: mandateFormData.selectedMandate,
      doneBy: decode?.display_name,
    })
      .then(res => { if (res?.data?.message === 'Success') { alert('successful'); onActionComplete?.(); window.location.reload(); } })
      .catch(() => { alert('Error While Updating'); });
  };

  const handleRefreshMandate = () => {
    axiosInstance.post('/nach/refresh', { userId: regenUserId, applicationId: regenAppId })
      .then(() => { alert('Refresh Success'); onActionComplete?.(); })
      .catch(err => { alert(err?.response?.data?.message); });
  };

  const handleRefreshUpiMandate = () => {
    axiosInstance.post('/nach/upi-refresh', { userId: regenUserId, applicationId: regenAppId })
      .then(() => { alert('Refresh Success'); onActionComplete?.(); window.location.reload(); })
      .catch(err => { alert(err?.response?.data?.message); });
  };

  const isUpiMandateDisabled = () => {
    if (decode?.role === 'admin' || decode?.role === 'crm') return false;
    if (decode?.email === 'sairamk@feemonk.com') return false;
    const restricted = ['Medvarsity', 'WhiteCoats'];
    return !restricted.includes(mandateFormData.instituteName);
  };

  const handleShowVerificationDetails = async (documentType) => {
    const appId = regenAppId;
    const uid = regenUserId;
    try {
      const res = await axiosInstance(`/verification-tab/user-details?applicationId=${appId}&userId=${uid}&documentType=${documentType}`);
      if (res?.status === 200) { setShowVerificationDetails(true); setUserVerificationData(res?.data?.data); }
    } catch (e) { setShowVerificationDetails(false); alert('Failed to get details.'); }
  };

  // ── modal backdrop ──────────────────────────────────────────────────────

  const Overlay = () => (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 999 }} />
  );

  // ── info row helper ──────────────────────────────────────────────────────

  const InfoRow = ({ label, value }) => (
    <div style={INFO_ROW}>
      <div style={LABEL_STYLE}>{label}</div>
      <div style={VALUE_STYLE}>{value ?? <span style={{ color: '#98a2b3' }}>—</span>}</div>
    </div>
  );

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <>

      {/* ── DigiLocker Modal ──────────────────────────────── */}
      {digiOpen && (
        <>
          <Overlay />
          <Modal style={{ width: '520px', borderRadius: '12px' }} isOpen={digiOpen}>
            <div style={HEADER}>
              <span style={HEADER_TITLE}>DigiLocker Details</span>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                {(decode?.role === 'crm' || decode?.role === 'admin') && (
                  <button
                    style={{ fontSize: '14px', fontWeight: '500', padding: '3px 10px', borderRadius: '6px', cursor: 'pointer', lineHeight: '1.6',marginRight:'5rem',
                      backgroundColor: isHovered ? '#FEEAEB' : '#fff', color: '#D32028', border: '1px solid #D32028' }}
                    onClick={digiStatus === 'Skip' ? enableDigiLocker : skipDigiLocker}
                    onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
                    {digiStatus === 'Skip' ? 'Enable' : 'Skip'} DigiLocker
                  </button>
                )}
                <CloseBtn onClick={() => setDigiOpen(false)} />
              </div>
            </div>
            <ModalBody style={{ padding: '14px' }}>
              <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '10px', marginBottom: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <InfoRow label="Name As Per DigiLocker" value={digiName} />
                <InfoRow label="Name As Per Application" value={digiApplicantName} />
                <div style={INFO_ROW}>
                  <div style={LABEL_STYLE}>DigiLocker Name Match Score</div>
                  <Pill value={parseFloat(digiNameScore) || null} threshold={80} />
                </div>
                <InfoRow label="Date of Birth" value={digiDob} />
                <div style={INFO_ROW}>
                  <div style={LABEL_STYLE}>Aadhaar Number</div>
                  <span style={{
                    backgroundColor: digiAadhaarMatch === 100 || digiAadhaarMatch == null ? '#ECFDF3' : '#FFF6E8',
                    color: digiAadhaarMatch === 100 || digiAadhaarMatch == null ? '#12B76A' : '#F8A31F',
                    borderRadius: '20px', padding: '2px 8px', fontSize: '14px', fontWeight: '600',
                  }}>{digiAadhaarId || '—'}</span>
                </div>
                <InfoRow label="Current Address (DigiLocker)" value={digiAddress} />
              </div>
              <Form>
                <Row>
                  <Col xs={6}>
                    <FormGroup style={{ marginBottom: 0 }}>
                      <Label style={LABEL_STYLE}>KID</Label>
                      <Input style={{ borderRadius: '6px', height: '32px', fontSize: '14px' }} type="text" placeholder="KID"
                        value={digiKID} onChange={e => setDigiKID(e.target.value)} />
                    </FormGroup>
                  </Col>
                  <Col xs={6}>
                    <FormGroup style={{ marginBottom: 0 }}>
                      <Label style={LABEL_STYLE}>Transaction ID</Label>
                      <Input style={{ borderRadius: '6px', height: '32px', fontSize: '14px' }} type="text" placeholder="Transaction ID"
                        value={digiTxnId} onChange={e => setDigiTxnId(e.target.value)} />
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
            <div style={FOOTER}>
              <button style={BTN_PRIMARY} onClick={digiRetrigger}>Refresh DigiLocker Status</button>
            </div>
          </Modal>
        </>
      )}

      {/* ── Selfie Modal ──────────────────────────────────── */}
      {selfieOpen && (
        <>
          <Overlay />
          <Modal style={{ width: '420px', borderRadius: '12px' }} isOpen={selfieOpen}>
            <div style={HEADER}>
              <span style={HEADER_TITLE}>Selfie Details</span>
              <CloseBtn onClick={() => setSelfieOpen(false)} />
            </div>
            <ModalBody style={{ padding: '14px' }}>
              <Form style={{ marginBottom: '12px' }}>
                <Row>
                  <Col xs={6}>
                    <FormGroup style={{ marginBottom: 0 }}>
                      <Label style={LABEL_STYLE}>KID</Label>
                      <Input style={{ borderRadius: '6px', height: '32px', fontSize: '14px' }} type="text" placeholder="KID"
                        value={selfieKID} onChange={e => setSelfieKID(e.target.value)} />
                    </FormGroup>
                  </Col>
                  <Col xs={6}>
                    <FormGroup style={{ marginBottom: 0 }}>
                      <Label style={LABEL_STYLE}>Transaction ID</Label>
                      <Input style={{ borderRadius: '6px', height: '32px', fontSize: '14px' }} type="text" placeholder="Transaction ID"
                        value={selfieTxnId} onChange={e => setSelfieTxnId(e.target.value)} />
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
              <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <InfoRow label="Face Liveness" value={selfieDetails?.selfieDetails?.faceLivenessResult} />
                <div style={INFO_ROW}>
                  <div style={LABEL_STYLE}>Face Liveness Score</div>
                  {selfieDetails?.selfieDetails?.faceLivenessScore != null
                    ? <Pill value={selfieDetails.selfieDetails.faceLivenessScore * 100} threshold={70} />
                    : <span style={{ color: '#98a2b3' }}>—</span>}
                </div>
                <div style={INFO_ROW}>
                  <div style={LABEL_STYLE}>Face Match</div>
                  {selfieDetails?.selfieDetails?.facematchScore != null
                    ? <Pill value={Number(selfieDetails.selfieDetails.facematchScore)} threshold={70} />
                    : <span style={{ color: '#98a2b3' }}>—</span>}
                </div>
                <InfoRow label="Face Source" value={selfieDetails?.selfieDetails?.facematchSource} />
                <InfoRow label="Geo-Tag Accuracy" value={selfieDetails?.selfieDetails?.geoTagAccuracy != null ? `${Number(selfieDetails.selfieDetails.geoTagAccuracy).toFixed(2)} %` : null} />
                <InfoRow label="Geo-Tag Address" value={selfieDetails?.selfieDetails?.geoTagAddress} />
              </div>
            </ModalBody>
            <div style={FOOTER}>
              <button style={BTN_SECONDARY} onClick={selfieReinitiate}>Re-Initiate Selfie</button>
              <button style={BTN_PRIMARY} onClick={selfieRetrigger}>Refresh Selfie</button>
            </div>
          </Modal>
        </>
      )}

      {/* ── Agreement Modal ───────────────────────────────── */}
      {agreementOpen && (
        <>
          <Overlay />
          <Modal style={{ width: '600px', borderRadius: '12px' }} isOpen={agreementOpen}>
            <div style={HEADER}>
              <span style={HEADER_TITLE}>Agreement Details</span>
              <div style={{ display: 'flex', gap: '14px', alignItems: 'center' ,marginRight:'1.5rem'}}>
                <button style={BTN_SECONDARY} onClick={() => inputRef.current.click()}>
                  <input ref={inputRef} style={{ display: 'none' }} type="file" onChange={uploadManualAgreement} />
                  Manual Agrmnt.
                </button>
                <LoadingButton onClick={generateAgreementCopy}
                  sx={{ textTransform: 'none', borderColor: '#d0d5dd', color: '#344054', background: '#fff', fontSize: '13px', padding: '5px 12px' }}
                  loading={agreementLoading} loadingIndicator="Loading…" variant="outlined">
                  Generate
                </LoadingButton>
              </div>
                              <CloseBtn onClick={() => setAgreementOpen(false)} />

            </div>
            <ModalBody style={{ padding: '16px' }}>
              {(agreementDocDetails || agreementInfo) ? (
                <div style={{ display: 'flex', gap: '24px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={INFO_ROW}>
                      <div style={LABEL_STYLE}>Aadhaar Suffix</div>
                      <span style={{
                        fontSize: '16px', fontWeight: '600', padding: '2px 10px', borderRadius: '20px',
                        color: agreementInfo?.aadhaarMatch === 100 || agreementInfo?.aadhaarMatch == null ? '#12B76A' : '#F8A31F',
                        backgroundColor: agreementInfo?.aadhaarMatch === 100 || agreementInfo?.aadhaarMatch == null ? '#ECFDF3' : '#FFF6E8',
                      }}>{agreementInfo?.aadhaar_suffix ?? '—'}</span>
                    </div>
                    <InfoRow label="Aadhaar Name" value={agreementInfo?.name} />
                    <InfoRow label="Display Name" value={agreementInfo?.display_name} />
                    <InfoRow label="Created At" value={agreementDocDetails?.createdAt} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={INFO_ROW}>
                      <div style={LABEL_STYLE}>Aadhaar Name Validation Score</div>
                      {agreementInfo?.name_validation_score != null ? (
                        <span style={{
                          fontSize: '16px', fontWeight: '600', padding: '2px 10px', borderRadius: '20px',
                          color: agreementInfo.name_validation_score >= 75 ? '#12B76A' : agreementInfo.name_validation_score >= 60 ? '#F8A31F' : '#D22129',
                          backgroundColor: agreementInfo.name_validation_score >= 75 ? '#ECFDF3' : agreementInfo.name_validation_score >= 60 ? '#FFF6E8' : '#FEEAEB',
                        }}>{agreementInfo.name_validation_score}</span>
                      ) : <span style={{ color: '#98a2b3' }}>—</span>}
                    </div>
                    <InfoRow label="Postal Code" value={agreementInfo?.postal_code} />
                    <InfoRow label="Done At" value={agreementDocDetails?.updatedAt} />
                  </div>
                </div>
              ) : (
                <p style={{ color: '#667085', textAlign: 'center', padding: '20px 0' }}>Information is not yet available</p>
              )}
            </ModalBody>
            <div style={FOOTER}>
              <button style={BTN_SECONDARY} onClick={retriggerAgreement}>Retrigger Agrmnt.</button>
              <div style={{ display: 'flex', gap: '8px' }}>
                {(decode?.role === 'crm' || decode?.role === 'admin') && (
                  <button style={BTN_SECONDARY}
                    onClick={agreementStatus === 'Skip' ? enableAgreement : skipAgreement}>
                    {agreementStatus === 'Skip' ? 'Enable' : 'Skip'} Agrmnt.
                  </button>
                )}
                {decode?.role !== 'institute_user' && decode?.role !== 'institute_admin' && decode?.role !== 'channel_user' && (
                  <button style={BTN_PRIMARY} onClick={downloadAgreement}>
                    <img src={downloadIcon} alt="" style={{ height: '14px', width: '16px', marginRight: '6px', verticalAlign: 'middle' }} />
                    Agrmnt.
                  </button>
                )}
              </div>
            </div>
          </Modal>
        </>
      )}

      {/* ── Choose Mandate Form ───────────────────────────── */}
      {chooseMandateOpen && (
        <>
          <Overlay />
          <Modal isOpen={chooseMandateOpen} style={{ borderRadius: '12px' }}>
            <div style={HEADER}>
              <span style={HEADER_TITLE}>Choose Mandate</span>
              <CloseBtn onClick={() => setChooseMandateOpen(false)} />
            </div>
            <ModalBody>
              <Form>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label style={LABEL_STYLE}>Application ID</Label>
                      <Input type="text" value={mandateFormData.applicationId} disabled />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label style={LABEL_STYLE}>Name</Label>
                      <Input type="text" value={mandateFormData.name} disabled />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label style={LABEL_STYLE}>Current Mandate Status</Label>
                      <Input type="text" value={mandateFormData.currentMandate} disabled />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label style={LABEL_STYLE}>Select New Mandate</Label>
                      <Input type="select" value={mandateFormData.selectedMandate}
                        onChange={e => setMandateFormData(prev => ({ ...prev, selectedMandate: e.target.value }))}>
                        <option value="">-- Select a mandate --</option>
                        {mandateOptions.map(opt => (
                          <option key={opt.value} value={opt.value} disabled={(opt.value === '16' || opt.value === '22') && isUpiMandateDisabled()}>
                            {opt.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  {mandateFormData.currentMandate === 'Rejected' && (
                    <Col md={12}>
                      <p style={{ color: 'red', fontSize: '14px' }}>{remark}</p>
                    </Col>
                  )}
                </Row>
              </Form>
            </ModalBody>
            <div style={FOOTER}>
              <button style={BTN_SECONDARY} onClick={() => setRefreshMandateOpen(true)}>Refresh Emandate</button>
              <button style={BTN_PRIMARY} onClick={submitMandate}>Submit</button>
            </div>
          </Modal>
        </>
      )}

      {/* ── Mandate Details (Razorpay) ────────────────────── */}
      {mandateOpen && (
        <>
          <Overlay />
          <Modal style={{ borderRadius: '12px' }} isOpen={mandateOpen}>
            <div style={HEADER}>
              <span style={HEADER_TITLE}>Mandate Details</span>
              <CloseBtn onClick={() => setMandateOpen(false)} />
            </div>
            <ModalBody style={{ padding: '16px' }}>
              {mandateDetails ? (
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {[
                    ['Name as per Application', mandateDetails.nameAsPerApplication],
                    ['Name as per Bank', mandateDetails.name],
                    ['Match Percent', mandateDetails.matchPercentage != null ? `${(Math.round(mandateDetails.matchPercentage * 100) / 100).toFixed(2)} %` : null],
                    ['Account Number', mandateDetails.accountNumber],
                    ['Account Type', mandateDetails.accountType],
                    ['IFSC Code', mandateDetails.ifsc],
                    ['Customer Mobile', mandateDetails.phone],
                    ['Mandate Type', mandateDetails.mandateType],
                    ['Customer ID', mandateDetails.customerId],
                    ['Token ID', mandateDetails.tokenId],
                    ['Payment ID', mandateDetails.paymentId],
                  ].map(([label, val]) => (
                    <div key={label} style={{ width: '50%', paddingRight: '12px' }}>
                      <InfoRow label={label} value={val} />
                    </div>
                  ))}
                  {mandateDetails.remark && (
                    <div style={{ width: '100%', paddingTop: '8px' }}>
                      <div style={LABEL_STYLE}>Reason for Rejection</div>
                      <div style={{ ...VALUE_STYLE, color: '#D32027' }}>{mandateDetails.remark}</div>
                    </div>
                  )}
                </div>
              ) : <p style={{ color: '#667085', textAlign: 'center' }}>Information is not available</p>}
            </ModalBody>
            <div style={FOOTER}>
              <button style={BTN_SECONDARY} onClick={() => setRefreshMandateOpen(true)}>Refresh Emandate</button>
              <button style={BTN_PRIMARY} onClick={() => { setMandateOpen(false); setMandateFormData({ applicationId: regenAppId || '', userId: regenUserId || '', name: regenName || '', currentMandate: mStatus || '', selectedMandate: '', instituteName: regenInstituteName || '' }); setChooseMandateOpen(true); }}>Choose Mandate</button>
            </div>
          </Modal>
        </>
      )}

      {/* ── UPI Mandate Details ───────────────────────────── */}
      {upiMandateOpen && (
        <>
          <Overlay />
          <Modal style={{ borderRadius: '12px' }} isOpen={upiMandateOpen}>
            <div style={HEADER}>
              <span style={HEADER_TITLE}>UPI Mandate Details</span>
              <CloseBtn onClick={() => setUpiMandateOpen(false)} />
            </div>
            <ModalBody style={{ padding: '16px' }}>
              {(upiMandateDetails || phonepeDetails) ? (
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {[
                    ['Name as per Application', regenName],
                    ['Beneficiary Name', upiMandateDetails?.beneficiaryName ?? phonepeDetails?.beneficiaryName],
                    ['Account Number', upiMandateDetails?.accountNumber ?? phonepeDetails?.accountNumber],
                    ['Customer VPA', upiMandateDetails?.customerVpa ?? phonepeDetails?.vpa],
                    ['IFSC', upiMandateDetails?.ifsc ?? phonepeDetails?.ifsc],
                    ['Mandate ID', upiMandateDetails?.mandateId],
                    ['Transaction ID', upiMandateDetails?.transactionId ?? phonepeDetails?.orderId],
                    ['Transaction Time', upiMandateDetails?.transactionTime ],
                    ['UMRN', upiMandateDetails?.umrn ?? phonepeDetails?.umn],
                  ].map(([label, val]) => (
                    <div key={label} style={{ width: '50%', paddingRight: '12px' }}>
                      <InfoRow label={label} value={val} />
                    </div>
                  ))}
                </div>
              ) : <p style={{ color: '#667085', textAlign: 'center' }}>Information is not available</p>}
            </ModalBody>
            <div style={FOOTER}>
              <button style={BTN_SECONDARY} onClick={() => setRefreshUpiMandateOpen(true)}>Refresh Emandate</button>
              <button style={BTN_PRIMARY} onClick={() => { setUpiMandateOpen(false); setMandateFormData({ applicationId: regenAppId || '', userId: regenUserId || '', name: regenName || '', currentMandate: mStatus || '', selectedMandate: '', instituteName: regenInstituteName || '' }); setChooseMandateOpen(true); }}>Choose Mandate</button>
            </div>
          </Modal>
        </>
      )}

      {/* ── Aadhaar Mandate Details ───────────────────────── */}
      {aadharMandateOpen && (
        <>
          <Overlay />
          <Modal style={{ borderRadius: '12px' }} isOpen={aadharMandateOpen}>
            <div style={HEADER}>
              <span style={HEADER_TITLE}>Aadhaar Mandate Details</span>
              <CloseBtn onClick={() => setAadharMandateOpen(false)} />
            </div>
            <ModalBody style={{ padding: '16px' }}>
              {aadharMandateDetails ? (
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {[
                    ['Name as per Application', regenName],
                    ['Beneficiary Name', aadharMandateDetails.beneficiaryName],
                    ['Account Number', aadharMandateDetails.accountNumber],
                    ['IFSC', aadharMandateDetails.ifsc],
                    ['Mandate ID', aadharMandateDetails.mandateId],
                    ['Transaction ID', aadharMandateDetails.transactionId],
                    ['UMRN', aadharMandateDetails.umrn],
                  ].map(([label, val]) => (
                    <div key={label} style={{ width: '50%', paddingRight: '12px' }}>
                      <InfoRow label={label} value={val} />
                    </div>
                  ))}
                </div>
              ) : <p style={{ color: '#667085', textAlign: 'center' }}>Information is not available</p>}
            </ModalBody>
            <div style={FOOTER}>
              <button style={BTN_PRIMARY} onClick={() => { setAadharMandateOpen(false); setMandateFormData({ applicationId: regenAppId || '', userId: regenUserId || '', name: regenName || '', currentMandate: mStatus || '', selectedMandate: '', instituteName: regenInstituteName || '' }); setChooseMandateOpen(true); }}>Choose Mandate</button>
            </div>
          </Modal>
        </>
      )}

      {/* ── Refresh Mandate Confirm ───────────────────────── */}
      {refreshMandateOpen && (
        <>
          <Overlay />
          <Modal style={{ borderRadius: '12px' }} isOpen={refreshMandateOpen}>
            <div style={HEADER}>
              <span style={HEADER_TITLE}>Refresh Emandate</span>
              <CloseBtn onClick={() => setRefreshMandateOpen(false)} />
            </div>
            <ModalBody>Click Refresh to initiate Emandate Refresh</ModalBody>
            <div style={FOOTER}>
              <button style={BTN_SECONDARY} onClick={() => setRefreshMandateOpen(false)}>Close</button>
              <button style={BTN_PRIMARY} onClick={handleRefreshMandate}>Refresh</button>
            </div>
          </Modal>
        </>
      )}

      {/* ── Refresh UPI Mandate Confirm ───────────────────── */}
      {refreshUpiMandateOpen && (
        <>
          <Overlay />
          <Modal style={{ borderRadius: '12px' }} isOpen={refreshUpiMandateOpen}>
            <div style={HEADER}>
              <span style={HEADER_TITLE}>Refresh UPI Emandate</span>
              <CloseBtn onClick={() => setRefreshUpiMandateOpen(false)} />
            </div>
            <ModalBody>Click Refresh to initiate UPI Emandate Refresh</ModalBody>
            <div style={FOOTER}>
              <button style={BTN_SECONDARY} onClick={() => setRefreshUpiMandateOpen(false)}>Close</button>
              <button style={BTN_PRIMARY} onClick={handleRefreshUpiMandate}>Refresh</button>
            </div>
          </Modal>
        </>
      )}

      {/* ── Fraud Modal ───────────────────────────────────── */}
      {fraudOpen && (
        <>
          <Overlay />
          <Modal isOpen={fraudOpen} style={{ maxWidth: '52vw', width: '52vw', borderRadius: '12px' }}>
            <div style={HEADER}>
              <span style={HEADER_TITLE}>Fraud Detection Indicators</span>
              <CloseBtn onClick={() => setFraudOpen(false)} />
            </div>
            <ModalBody>
              <div style={{ display: 'flex', gap: '16px' }}>
                {/* Left Table */}
                <Table style={{ border: '1px solid #dee2e6', flex: 1 }}>
                  <thead><tr>
                    <th style={{ border: '1px solid #dee2e6' }}>Indicator</th>
                    <th style={{ border: '1px solid #dee2e6' }}>Status</th>
                  </tr></thead>
                  <tbody>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6' }}>Agreement Name match</td>
                      <td style={{ border: '1px solid #dee2e6', cursor: 'pointer' }}
                        onClick={() => openAgreement({ applicationId: regenAppId, userId: regenUserId, status: null })}>
                        {fraudDetails?.agreementMatchScore != null
                          ? <Pill value={fraudDetails.agreementMatchScore} threshold={70} />
                          : ''}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6' }}>DigiLocker Name match</td>
                      <td style={{ border: '1px solid #dee2e6', cursor: 'pointer' }}
                        onClick={() => openAgreement({ applicationId: regenAppId, userId: regenUserId, status: null })}>
                        {fraudDetails?.digilockerNameMatchScore != null
                          ? <Pill value={fraudDetails.digilockerNameMatchScore} threshold={80} />
                          : ''}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6' }}>Face Match</td>
                      <td style={{ border: '1px solid #dee2e6', cursor: 'pointer' }}
                        onClick={() => openSelfie({ applicationId: regenAppId, userId: regenUserId })}>
                        {fraudDetails?.faceMatchScore != null
                          ? <Pill value={fraudDetails.faceMatchScore} threshold={70} />
                          : ''}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6' }}>Location within 100KM</td>
                      <td style={{ border: '1px solid #dee2e6', color: fraudDetails?.distanceRange > 100 ? '#D22129' : '#12B76A' }}>
                        {fraudDetails?.distanceRange ? `${fraudDetails.distanceRange} KM` : ''}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6' }}>Name Match in Bank (E-Mandate)</td>
                      <td style={{ border: '1px solid #dee2e6', cursor: 'pointer' }}
                        onClick={() => openMandate({ applicationId: regenAppId, userId: regenUserId, name: regenName, status: mStatus })}>
                        {fraudDetails?.bankAccountNameMatchScore != null
                          ? <Pill value={fraudDetails.bankAccountNameMatchScore} threshold={70} />
                          : ''}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6' }}>Pan Name Match</td>
                      <td style={{ border: '1px solid #dee2e6' }}>
                        {fraudDetails?.panMatchScore != null
                          ? <Pill value={fraudDetails.panMatchScore} threshold={70} />
                          : ''}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6' }}>Mandate Provider</td>
                      <td style={{ border: '1px solid #dee2e6', color: '#667085' }}>
                        {fraudDetails?.mandateProvider === 1 ? 'DigioUpiMandate' : fraudDetails?.mandateProvider === 2 ? 'RazorpayMandate' : ''}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {/* Right Table */}
                <Table style={{ border: '1px solid #dee2e6', flex: 1 }}>
                  <thead><tr>
                    <th style={{ border: '1px solid #dee2e6' }}>Indicator</th>
                    <th style={{ border: '1px solid #dee2e6' }}>Status</th>
                  </tr></thead>
                  <tbody>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6' }}>Consent Range</td>
                      <td style={{ border: '1px solid #dee2e6', color: fraudDetails?.consentRange > 100 ? '#D22129' : '#12B76A' }}>
                        {fraudDetails?.consentRange != null ? `${fraudDetails.consentRange} KM` : ''}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6' }}>Name Match in BankStatement</td>
                      <td style={{ border: '1px solid #dee2e6' }}>
                        {fraudDetails?.bankStatementMatchScore != null
                          ? <Pill value={fraudDetails.bankStatementMatchScore} threshold={70} />
                          : ''}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6' }}>Bank Account Number Match (EM&BS)</td>
                      <td style={{ border: '1px solid #dee2e6' }}>
                        {fraudDetails?.bankAccountNumberMatchScore != null && (
                          fraudDetails.bankAccountNumberMatchScore === 100 ? <Circle pass={true} /> :
                          fraudDetails.bankAccountNumberMatchScore === 0 ? <Circle pass={false} /> :
                          fraudDetails.bankAccountNumberMatchScore === 1 ? <span style={{ fontWeight: 'bold' }}>NA</span> : null
                        )}
                      </td>
                    </tr>
                    {[
                      ['Pan Kyc Match', fraudDetails?.panKyc, 2],
                      ['Ckyc Kyc Match', fraudDetails?.ckycKyc, 5],
                      ['DigiLocker Kyc Match', fraudDetails?.digioKyc, 6],
                      ['Cibil Kyc Match', fraudDetails?.cibilKyc, 3],
                    ].map(([label, val, docType]) => (
                      <tr key={label}>
                        <td style={{ border: '1px solid #dee2e6' }}>{label}</td>
                        <td style={{ border: '1px solid #dee2e6' }}>
                          {val != null && (
                            <Tooltip
                              title={val === 1 ? 'Name Score Match > 75, DOB and Gender Matched, Click to see details' : 'Either Name, DOB, or Gender was not matched, Click to see details'}
                              arrow placement="top">
                              <span style={{ display: 'inline-block', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: val === 1 ? '#12B76A' : '#D22129', cursor: 'pointer' }}
                                onClick={() => handleShowVerificationDetails(docType)} />
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td style={{ border: '1px solid #dee2e6' }}>WhatsApp Number</td>
                      <td style={{ border: '1px solid #dee2e6' }}>
                        <Tooltip title={fraudDetails?.whatsappNumber === '1' ? 'Whatsapp Number Collected' : 'No Whatsapp Number'} arrow placement="top">
                          <span style={{ display: 'inline-block', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: fraudDetails?.whatsappNumber === '1' ? '#12B76A' : '#D22129', cursor: 'pointer' }} />
                        </Tooltip>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </ModalBody>
          </Modal>
        </>
      )}

      {/* ShowDetails for KYC verification */}
      <ShowDetails
        open={showVerificationDetails}
        userVerificationData={userVerificationData}
        setShowVerificationDetails={setShowVerificationDetails}
      />
    </>
  );
});

export default IntegrationModals;
